import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { Accordion } from '@/components/Accordion/Accordion';
import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionTemplateKind } from '@/types/schema';

import { DispositionScheme } from '../../dispositiveProvisions.types';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm.types';
import { useRecipientOptionsParams } from '../hooks/useRecipientOptions';
import {
  useDeathOrderAwareClient,
  useDispositionScheme,
} from '../hooks/utilityHooks';
import { DispositiveProvisionsRecipient } from './DispositiveProvisionsRecipient';
import { DispositiveProvisionsRecipientTemplateRecipientConfiguration } from './DispositiveProvisionsRecipient.types';
import {
  getRecipientValidationError,
  getTemplateRulesForTemplateType,
} from './DispositiveProvisionsRecipient.utils';
import { useRecipientAccordionProps } from './DraggableListItemRecipient.utils';

interface DraggableListItemRecipientProps {
  index: number;
  Draghandle: React.ReactNode;
  defaultExpanded?: boolean;
  onRemove?: (() => void) | null;
  templateType?: DispositiveProvisionTemplateKind;
  recipientConfiguration?: Partial<DispositiveProvisionsRecipientTemplateRecipientConfiguration>;
  emptyRecipientOptionDisplay?: string;
  isOptional?: boolean;
  /**
   * This field gets passed down from the PrimaryClientDeathSectionRecipients
   * component.
   */
  deathEvent?: PrimaryClientDeathEvent;
  /**
   * Unique ID for the accordion to enable collapse behavior via AccordionContext.
   * This is the useFieldArray-assigned _id for V2 forms.
   */
  accordionId?: string;
}

function getRecipientsBasePath(deathEvent?: PrimaryClientDeathEvent) {
  if (deathEvent === undefined) {
    // The V1 form and the template editor store recipients directly under the
    // dispositive provisions form instead of under a death event.
    return `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const;
  }

  const deathKey =
    deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
      ? 'firstDeath'
      : 'secondDeath';
  return `${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${deathKey}.recipients` as const;
}

interface CollapsedRecipientValidationProps {
  index: number;
  deathEvent?: PrimaryClientDeathEvent;
  recipientIsRequired: boolean;
}

/**
 * Keeps submit validation registered while the full recipient editor is
 * unmounted. React Hook Form does not validate unmounted controllers on submit.
 */
function CollapsedRecipientValidation({
  index,
  deathEvent,
  recipientIsRequired,
}: CollapsedRecipientValidationProps) {
  const { control, setValue } = useFormContext<CombinedFormShape>();
  const recipientsBasePath = getRecipientsBasePath(deathEvent);
  const recipient = useWatch({
    control,
    name: `${recipientsBasePath}.${index}` as const satisfies CombinedPaths,
  }) as DispositiveProvisionsFormRecipient | undefined;
  const recipientOptionsParams = useRecipientOptionsParams({ deathEvent });
  const unavailableRecipientOptions =
    recipientOptionsParams.deadPrimaryClientIds.map((recipientId) => ({
      display: '',
      value: recipientId,
      disabled: true,
    }));
  const validationFieldName =
    `${recipientsBasePath}.${index}._templateSlotKind` as const satisfies CombinedPaths;

  useEffect(() => {
    // Formula values always grow with inflation. This normalization must run
    // even when the full recipient editor is unmounted.
    if (
      recipient?.dispositionKind_ButtonGroup === 'other' &&
      recipient.growByInflation !== true
    ) {
      setValue(
        `${recipientsBasePath}.${index}.growByInflation` as const satisfies CombinedPaths,
        true
      );
    }
  }, [index, recipient, recipientsBasePath, setValue]);

  return (
    <Controller
      name={validationFieldName}
      control={control}
      rules={{
        validate: {
          collapsedRecipient: () =>
            getRecipientValidationError({
              recipient,
              recipientOptions: unavailableRecipientOptions,
              recipientIsRequired,
            }),
        },
      }}
      render={() => {
        // This controller only registers validation. The accordion header owns
        // the visible summary, so this render intentionally has no content.
        return <></>;
      }}
    />
  );
}

export function DraggableListItemRecipient({
  index,
  Draghandle,
  onRemove,
  defaultExpanded,
  templateType,
  recipientConfiguration: externalRecipientConfiguration,
  emptyRecipientOptionDisplay,
  isOptional = false,
  deathEvent,
  accordionId,
}: DraggableListItemRecipientProps) {
  const {
    formState: { errors },
    clearErrors,
    trigger,
  } = useFormContext<CombinedFormShape>();
  const dispositionScheme = useDispositionScheme();
  const firstDeadGrantor = useDeathOrderAwareClient(
    deathEvent ? { deathEvent } : undefined
  );
  const { availableStateEstateTaxes } = useIRSConstants();
  const { recipientConfiguration, templateRules } =
    getTemplateRulesForTemplateType(
      templateType,
      externalRecipientConfiguration
    );

  const hasStateTax = useMemo(() => {
    if (!firstDeadGrantor) return false;

    return Boolean(
      availableStateEstateTaxes?.includes(firstDeadGrantor.stateCode!)
    );
  }, [availableStateEstateTaxes, firstDeadGrantor]);

  const accordionProps = useRecipientAccordionProps(
    index,
    hasStateTax,
    recipientConfiguration,
    isOptional,
    Draghandle,
    deathEvent
  );

  const recipientsBasePath = getRecipientsBasePath(deathEvent);
  const recipientErrors = (() => {
    if (deathEvent === undefined) {
      return errors[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]?.recipients?.[index];
    }
    const deathKey =
      deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
        ? 'firstDeath'
        : 'secondDeath';
    return errors[DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE]?.[deathKey]
      ?.recipients?.[index];
  })();
  const recipientHasFieldError = Boolean(recipientErrors);
  const collapsedValidationError = recipientErrors?._templateSlotKind?.message;

  const handleEditorEntered = useCallback(async () => {
    if (!collapsedValidationError) return;

    await trigger(
      `${recipientsBasePath}.${index}` as const satisfies CombinedPaths
    );
    clearErrors(
      `${recipientsBasePath}.${index}._templateSlotKind` as const satisfies CombinedPaths
    );
  }, [
    clearErrors,
    collapsedValidationError,
    index,
    recipientsBasePath,
    trigger,
  ]);

  const defaultExpansion = useMemo(() => {
    if (defaultExpanded) return true;
    return recipientHasFieldError ? true : undefined;
  }, [defaultExpanded, recipientHasFieldError]);

  return (
    <Accordion
      labelContainerProps={{
        spacing: 0,
        justifyContent: 'center',
      }}
      variant="filled"
      isDefaultExpanded={defaultExpansion}
      id={accordionId}
      data-testid={`DraggableListItemRecipient-${index}`}
      slotProps={{
        transition: {
          unmountOnExit: true,
          onEntered: handleEditorEntered,
        },
      }}
      {...accordionProps}
      title={
        <>
          <CollapsedRecipientValidation
            index={index}
            deathEvent={deathEvent}
            recipientIsRequired={
              templateRules.templateRequiresRecipient && !isOptional
            }
          />
          {accordionProps.title}
        </>
      }
      description={collapsedValidationError ?? accordionProps.description}
      // Per design spec, recipient cards use NAVY[50] (#F6F9FB) background
      // rather than the filled-light-gray variant's GRAY[50], matching the
      // beneficiary-designations recipient cards.
      sx={{ '&.MuiAccordion-root': { backgroundColor: COLORS.NAVY[50] } }}
    >
      <DispositiveProvisionsRecipient
        idx={index}
        deathEvent={deathEvent}
        deadPrimaryClientId={firstDeadGrantor.id}
        isSecondDeath={
          deathEvent !== undefined
            ? deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
            : dispositionScheme === DispositionScheme.UPON_SECOND_DEATH
        }
        onRemove={onRemove}
        templateType={templateType}
        recipientConfiguration={externalRecipientConfiguration}
        emptyRecipientOptionDisplay={emptyRecipientOptionDisplay}
        isOptional={isOptional}
      />
    </Accordion>
  );
}
